import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { formatSyncType } from '../../utils/formatUtils';

// ── Helpers ──────────────────────────────────────────────────────────────────

const formatTimestamp = (ts) => {
  if (!ts) return '';
  try {
    const d = new Date(ts);
    return d.toLocaleTimeString('en-US', { hour12: false });
  } catch {
    return String(ts);
  }
};

const getMsgClass = (message) => {
  if (!message) return '';
  const msg = message.toLowerCase();
  if (msg.includes('error') || msg.includes('failed') || msg.includes('fail')) return 'is-error';
  if (msg.includes('warn') || msg.includes('warning')) return 'is-warn';
  if (msg.includes('success') || msg.includes('completed') || msg.includes('done')) return 'is-success';
  if (msg.includes('info') || msg.startsWith('[info]')) return 'is-info';
  return '';
};

// ── TerminalViewer ────────────────────────────────────────────────────────────

const ALL_RECENT = '__all__';

const TerminalViewer = ({ syncHistory = [] }) => {
  const bodyRef = useRef(null);
  const [atBottom, setAtBottom] = useState(true);
  const [selectedSyncId, setSelectedSyncId] = useState(ALL_RECENT);

  // ── Build log entries ──────────────────────────────────────────────────────

  const logEntries = useMemo(() => {
    if (selectedSyncId === ALL_RECENT) {
      // Combine logs from the 5 most recent syncs
      const recent = [...syncHistory].slice(0, 5);
      const combined = [];
      for (const sync of recent) {
        if (Array.isArray(sync.logs) && sync.logs.length > 0) {
          sync.logs.forEach((entry) =>
            combined.push({ ...entry, _syncId: sync.id, _syncType: sync.type })
          );
        }
      }
      // Sort by time ascending
      combined.sort((a, b) => {
        if (!a.time && !b.time) return 0;
        if (!a.time) return -1;
        if (!b.time) return 1;
        return new Date(a.time) - new Date(b.time);
      });
      return combined;
    }

    const sync = syncHistory.find((s) => String(s.id) === String(selectedSyncId));
    if (!sync || !Array.isArray(sync.logs)) return [];
    return [...sync.logs].sort((a, b) => {
      if (!a.time && !b.time) return 0;
      if (!a.time) return -1;
      if (!b.time) return 1;
      return new Date(a.time) - new Date(b.time);
    });
  }, [syncHistory, selectedSyncId]);

  // ── Auto-scroll to bottom ──────────────────────────────────────────────────

  const scrollToBottom = useCallback(() => {
    if (bodyRef.current) {
      bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
    }
  }, []);

  useEffect(() => {
    if (atBottom) {
      scrollToBottom();
    }
  }, [logEntries, atBottom, scrollToBottom]);

  const handleScroll = () => {
    if (!bodyRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = bodyRef.current;
    const isNearBottom = scrollHeight - scrollTop - clientHeight < 40;
    setAtBottom(isNearBottom);
  };

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="terminal-viewer-card theme-card">
      {/* Toolbar */}
      <div className="terminal-viewer-toolbar">
        <h2 className="terminal-viewer-title">
          <i className="bi bi-terminal" style={{ color: 'var(--accent-green)' }}></i>
          Terminal Log
        </h2>

        {/* Source selector */}
        <select
          className="terminal-viewer-source-select"
          value={selectedSyncId}
          onChange={(e) => setSelectedSyncId(e.target.value)}
        >
          <option value={ALL_RECENT}>Recent (last 5)</option>
          {syncHistory.map((sync) => (
            <option key={sync.id} value={String(sync.id)}>
              #{sync.id} — {formatSyncType(sync.type)}
            </option>
          ))}
        </select>

        <div className="terminal-viewer-spacer"></div>

        <span className="terminal-viewer-count">
          {logEntries.length} {logEntries.length === 1 ? 'entry' : 'entries'}
        </span>

        {!atBottom && (
          <button
            className="terminal-viewer-scroll-btn"
            onClick={() => {
              setAtBottom(true);
              scrollToBottom();
            }}
          >
            <i className="bi bi-arrow-down"></i>
            Jump to bottom
          </button>
        )}
      </div>

      {/* Terminal body */}
      <div
        className="terminal-viewer-body"
        ref={bodyRef}
        onScroll={handleScroll}
      >
        {logEntries.length === 0 ? (
          <div className="terminal-empty">
            <i className="bi bi-terminal terminal-empty-icon"></i>
            <span>No log entries available for the selected source.</span>
          </div>
        ) : (
          logEntries.map((entry, idx) => (
            <div key={idx} className="terminal-log-entry">
              <span className="terminal-log-line-num">{idx + 1}</span>
              {entry.time && (
                <span className="terminal-log-timestamp">
                  {formatTimestamp(entry.time)}
                </span>
              )}
              <span className={`terminal-log-msg ${getMsgClass(entry.message)}`}>
                {entry.message}
              </span>
            </div>
          ))
        )}

        {/* Floating scroll-to-bottom button */}
        {!atBottom && (
          <button
            className="terminal-scroll-bottom-fab"
            onClick={() => {
              setAtBottom(true);
              scrollToBottom();
            }}
          >
            <i className="bi bi-arrow-down-circle"></i>
            Bottom
          </button>
        )}
      </div>
    </div>
  );
};

export default TerminalViewer;
